import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, FileText, Calculator, BarChart3, 
  Building, Phone, ArrowRight, Star, Shield,
  Clock, Users, Briefcase, Landmark, Award
} from 'lucide-react';

const Home = () => {
  const services = [
    {
      id: 'gst',
      icon: <FileText size={40} />,
      title: 'GST Services',
      description: 'GST Registration, Return Filing, GST Notice Handling, and Compliance Advisory',
      link: '/services/gst'
    },
    {
      id: 'income-tax',
      icon: <Calculator size={40} />,
      title: 'Income Tax',
      description: 'ITR Filing, Tax Planning, Scrutiny & Notice Representation for all categories',
      link: '/services/income-tax'
    },
    {
      id: 'audit',
      icon: <BarChart3 size={40} />,
      title: 'Audit & Assurance',
      description: 'Statutory Audit, Tax Audit, Internal Audit with complete compliance',
      link: '/services/audit'
    },
    {
      id: 'accounting',
      icon: <Building size={40} />,
      title: 'Accounting & Compliance',
      description: 'Bookkeeping, TDS Returns, Payroll, and ROC Compliance services',
      link: '/services/accounting'
    },
    {
      id: 'advisory',
      icon: <Briefcase size={40} />,
      title: 'Business Advisory',
      description: 'Company Registration, Financial Planning, Loan Assistance & Business Structuring',
      link: '/services/advisory'
    },
    {
      id: 'financial',
      icon: <Landmark size={40} />,
      title: 'Financial Services',
      description: 'Business Loans, LAP, Personal Loans, Housing Loans & Working Capital financing',
      link: '/services/financial'
    }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'Business Owner',
      content: 'Highly professional and knowledgeable CA. Provides timely GST and tax filing services with complete clarity.',
      rating: 5
    },
    {
      name: 'Priya Sharma',
      role: 'Entrepreneur',
      content: 'Very supportive and responsive. Helped us manage compliance without stress. Strongly recommended in Muzaffarnagar.',
      rating: 5
    },
    {
      name: 'Amit Verma',
      role: 'Company Director',
      content: 'Excellent advisory and tax planning guidance. Transparent and trustworthy services.',
      rating: 5
    },
    {
      name: 'Sandeep Agarwal',
      role: 'Manufacturing Business',
      content: 'Outstanding service for audit and statutory compliance. Always available for consultation and provides expert guidance.',
      rating: 5
    },
    {
      name: 'Neha Gupta',
      role: 'Retail Shop Owner',
      content: 'Very helpful in GST registration and monthly return filing. Explains everything clearly and makes compliance easy.',
      rating: 5
    },
    {
      name: 'Vikram Singh',
      role: 'Salaried Professional',
      content: 'Best CA for ITR filing. Helped me maximize tax savings with proper planning. Highly recommended for individual tax payers.',
      rating: 5
    }
  ];

  const whyChooseUs = [
    { icon: <Shield size={24} />, title: 'Experienced CA', text: 'Qualified professional with proven track record' },
    { icon: <Clock size={24} />, title: 'Timely Compliance', text: 'Never miss a deadline or filing date' },
    { icon: <CheckCircle size={24} />, title: 'Accuracy', text: 'Error-free financial reporting & tax filing' },
    { icon: <Users size={24} />, title: 'Client-Focused', text: 'Personalized attention to every client' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Gupta Piyush & Company <br />
              <span className="hero-highlight">Chartered Accountants</span>
            </h1>
            <p className="hero-subtitle">
              Expert Solutions in GST, Income Tax, Audit & Business Advisory
            </p>
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary">
                Get FREE Consultation
              </Link>
              <a href="tel:+916396550622" className="btn btn-secondary">
                <Phone size={20} />
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats Section */}
      <section className="trust-stats-section">
        <div className="container">
          <div className="trust-stats-grid">
            <div className="trust-stat-card">
              <div className="trust-stat-icon">
                <Award size={32} />
              </div>
              <div className="trust-stat-number">6+</div>
              <div className="trust-stat-label">Years Experience</div>
            </div>
            
            <div className="trust-stat-card">
              <div className="trust-stat-icon">
                <Users size={32} />
              </div>
              <div className="trust-stat-number">500+</div>
              <div className="trust-stat-label">Happy Clients</div>
            </div>
            
            <div className="trust-stat-card">
              <div className="trust-stat-icon">
                <Star size={32} />
              </div>
              <div className="trust-stat-number">5.0★</div>
              <div className="trust-stat-label">104 Reviews on JustDial</div>
            </div>
            
            <div className="trust-stat-card">
              <div className="trust-stat-icon">
                <Shield size={32} />
              </div>
              <div className="trust-stat-number">ICAI</div>
              <div className="trust-stat-label">Certified Member</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Snapshot */}
      <section className="about-snapshot">
        <div className="container">
          <div className="about-content">
            <h2 className="section-title">Gupta Piyush & Company</h2>
            <p className="about-text">
              A professionally managed Chartered Accountancy firm offering reliable and 
              result-oriented financial solutions for individuals, startups, SMEs, and 
              established businesses.
            </p>
            <div className="about-values">
              <div className="value-item">
                <CheckCircle className="value-icon" />
                <span>Accuracy</span>
              </div>
              <div className="value-item">
                <CheckCircle className="value-icon" />
                <span>Timely Compliance</span>
              </div>
              <div className="value-item">
                <CheckCircle className="value-icon" />
                <span>Strategic Tax Planning</span>
              </div>
              <div className="value-item">
                <CheckCircle className="value-icon" />
                <span>Long-term Relationships</span>
              </div>
            </div>
            <Link to="/about" className="btn btn-outline">
              Learn More About Us
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <h2 className="section-title-center">Our Core Services</h2>
          <p className="section-subtitle">
            Comprehensive financial solutions tailored to your business needs
          </p>
          
          <div className="services-grid">
            {services.map((service) => (
              <div key={service.id} className="service-card">
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <Link to={service.link} className="service-link">
                  Learn More <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>

          <div className="services-cta">
            <Link to="/services" className="btn btn-primary">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose-section">
        <div className="container">
          <h2 className="section-title-center">Why Clients Trust Us</h2>
          <div className="why-grid">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="why-card">
                <div className="why-icon">{item.icon}</div>
                <h3 className="why-title">{item.title}</h3>
                <p className="why-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title-center">What Our Clients Say</h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} fill="#FF8C42" stroke="#FF8C42" />
                  ))}
                </div>
                <p className="testimonial-content">"{testimonial.content}"</p>
                <div className="testimonial-author">
                  <p className="testimonial-name">{testimonial.name}</p>
                  <p className="testimonial-role">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-text">
              Let us handle your financial compliance while you focus on growing your business
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-white">
                Schedule Consultation
              </Link>
              <a href="tel:+916396550622" className="btn btn-outline-white">
                <Phone size={20} />
                +91-6396550622
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact Banner */}
      <section className="emergency-contact-banner">
        <div className="container">
          <div className="emergency-content">
            <div className="emergency-icon">
              <Phone size={32} />
            </div>
            <div className="emergency-text">
              <h3 className="emergency-title">Need Urgent Help with Tax Notice or GST Issues?</h3>
              <p className="emergency-subtitle">Our experts are available for emergency consultations</p>
            </div>
            <div className="emergency-actions">
              <a href="tel:+916396550622" className="emergency-btn">
                Call Now: +91-6396550622
              </a>
              <a href="tel:+917417215103" className="emergency-btn-alt">
                Alt: +91-7417215103
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
